/* eslint-disable react/destructuring-assignment */
import { fetchServices } from 'features/services/business/Services.api';
import Head from 'next/head';
import { FreeFieldCode } from 'pleinchamp-api-client';
import React, { createContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePlcApiCall } from '@api/business/api.hook';
import { FreeFieldApi } from '@api/business/FreeFieldContent.api';
import { getUserNotifications } from '@api/business/UserApi.utils';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { useForecastParamCodes } from '@meteo/business/Forecast.hook';
import {
  orderedDefaultForecastParamCodes,
  orderedDefaultForecastParamCodes15H,
  orderedDefaultForecastParamCodes36H,
} from '@meteo/business/forecastParamCodes.utils';
import { getCityFromUrl } from '@meteo/business/Location.utils';
import { useLocation } from '@meteo/business/LocationSelector.hook';
import { fetch15DaysForecasts, fetch36HoursForecasts, fetchForecasts, fetchMeteoNews } from '@meteo/business/Meteo.api';
import { MeteoHomeTabs } from '@meteo/business/Meteo.types';
import { computeMeteoInfoFromDateAndPostion, getLastCity, useSelectNewCity } from '@meteo/business/meteo.utils';
import { MeteoHomeLayout } from '@meteo/components/MeteoHomeLayout/MeteoHomeLayout.component';
import { WeatherContext, WeatherPageProps } from '@meteo/types';
import { RootState } from '@store/store';
import { usePlcRouter } from '@utils/customHooks';
import { isServer } from '@utils/server';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString, return404, urlEnum } from '@utils/url';
import { PlcNextPageContext } from 'features/business/app.types';

function fetchFreeField() {
  return FreeFieldApi.fetchForCode(FreeFieldCode.Meteo);
}

export const MeteoHomeContext = createContext<WeatherContext>({
  city: { isValidating: false },
  defaultTab: { isValidating: false },
  forecasts: {},
  forecasts15Days: {},
  forecasts36Hours: {},
  freeFieldContent: { isValidating: false },
  news: {},
  notifications: { isValidating: false },
  paramCodes: { isValidating: false },
  servicesContent: {},
});

function MeteoHome(props: WeatherPageProps) {
  const { selectedCity: city, onSelectNewCity: setCity } = useSelectNewCity(
    `${urlEnum.meteo}/[type]/[locality]`,
    props.city
  );
  // Pour pouvoir mettre à jour les données dans le Context.Provider
  const [forecasts36Hours, setForecasts36Hours] = useState(props.forecasts36Hours);
  const [forecasts15Days, setForecasts15Days] = useState(props.forecasts15Days);
  const [forecasts, setForecasts] = useState(props.forecasts);

  const { coordinates, isGeolocationLoading, geolocationError, city: reversedCity } = useLocation(city);

  const [defaultTab, setDefaultTab] = useState(props.defaultTab || MeteoHomeTabs.ThirtySixHours);
  let initialParamCodes;
  if (defaultTab === MeteoHomeTabs.ThirtySixHours) {
    initialParamCodes = orderedDefaultForecastParamCodes36H;
  } else if (defaultTab === MeteoHomeTabs.FifteenDays) {
    initialParamCodes = orderedDefaultForecastParamCodes15H;
  }

  const paramCodes = useForecastParamCodes(initialParamCodes);
  const {
    location: { favoriteLocations },
    auth: { isAuthenticated },
  } = useSelector((state: RootState) => state);

  const freeFieldContent = usePlcApiCall(fetchFreeField, [], { initialData: props.freeFieldContent });
  const notifications = usePlcApiCall(getUserNotifications, [favoriteLocations, isAuthenticated]);
  const additionalInfos = coordinates
    ? computeMeteoInfoFromDateAndPostion(new Date(), coordinates.latitude, coordinates.longitude)
    : undefined;

  const { isFallback } = usePlcRouter();
  if (isFallback) {
    return <FullPageLoader visible={true} />;
  }

  return (
    <MeteoHomeContext.Provider
      value={{
        additionalInfos,
        city: {
          data: city,
          isValidating: isGeolocationLoading || isServer(),
          setData: setCity,
        },
        coordinates,
        defaultTab: {
          data: defaultTab,
          isValidating: false,
          setData: setDefaultTab,
        },
        forecasts: { initialData: forecasts, setData: setForecasts },
        forecasts15Days: { initialData: forecasts15Days, setData: setForecasts15Days },
        forecasts36Hours: { initialData: forecasts36Hours, setData: setForecasts36Hours },
        freeFieldContent,
        geolocationError,
        news: { initialData: props.news },
        notifications,
        paramCodes: {
          data: paramCodes,
          isValidating: false,
        },
        reversedCity,
        servicesContent: { initialData: props.servicesContent },
      }}
    >
      <MeteoHomeLayout />
      <Head>
        <meta content="max-snippet:-1, max-image-preview:large, max-video-preview:-1" name="robots" />
      </Head>
    </MeteoHomeContext.Provider>
  );
}

// Warning:  when using getStaticProps we end up sending old data to users because it behaves like StaleWhileRevalidate (sending what it has while refetching new data)
MeteoHome.getInitialProps = async (ctx: PlcNextPageContext) => {
  const { query } = ctx;

  const city = (await getCityFromUrl(query?.locality)) || getLastCity(ctx);

  const defaultTab = query?.type ? (castUrlParamToString(query.type) as MeteoHomeTabs) : MeteoHomeTabs.ThirtySixHours;
  if (!Object.values(MeteoHomeTabs).includes(defaultTab)) {
    return404(ctx);
  }
  const pageDefaultState = {
    city,
    defaultTab,
    namespacesRequired: [...defaultNamespaces, 'meteo'],
  };

  const coordinates = city?.geometry || undefined;
  const props = await Promise.all([
    fetchMeteoNews(),
    fetchFreeField(),
    coordinates ? fetchForecasts(coordinates, orderedDefaultForecastParamCodes) : undefined,
    coordinates ? fetch36HoursForecasts(coordinates, orderedDefaultForecastParamCodes36H) : undefined,
    coordinates ? fetch15DaysForecasts(coordinates, orderedDefaultForecastParamCodes15H, false) : undefined,
    fetchServices(),
  ])
    .then(([news, freeFieldContent, forecasts, forecasts36Hours, forecasts15Days, servicesContent]) => {
      return {
        ...pageDefaultState,
        forecasts,
        forecasts15Days,
        forecasts36Hours,
        freeFieldContent,
        news,
        servicesContent,
      };
    })
    .catch(() => pageDefaultState);
  return props;
};

export default MeteoHome;
